import { t } from "@lingui/macro";
import { SqlOperator } from "@src/__generated__/graphql";
import {
  TimeTrackingProjectReportOrderByColumnEnum,
  TimeTrackingReportsWhereColumn,
} from "@src/__generated__/urql-graphql";
import type { TimeReportRow } from "@src/components/modules/projects/time-report/columns";
import type {
  ProjectBreakdownGroupModel,
  ProjectBreakdownItemModel,
} from "@src/components/modules/projects/time-report/models";
import type { TimeTrackingModel } from "@src/components/modules/projects/time-report/Store";
import type { TimeReportFilter } from "@src/components/modules/time-tracking/Report/time-report-url";
import { find, groupBy, sumBy } from "lodash";

// Transform grouped reports into DataTable row format
export function transformReportsToRows(
  reports: TimeTrackingModel[],
  column: TimeTrackingProjectReportOrderByColumnEnum,
): TimeReportRow[] {
  const pickLabels = (
    item: TimeTrackingModel,
    col: TimeTrackingProjectReportOrderByColumnEnum,
  ) => {
    switch (col) {
      case TimeTrackingProjectReportOrderByColumnEnum.OurWorkBudgetItemTitle:
        return {
          label: item?.user_full_name ?? "",
          groupLabel: item.our_work_budget_item_title ?? "",
        };
      case TimeTrackingProjectReportOrderByColumnEnum.UserFullName:
        return {
          label: item?.time_tracking_work_type_title ?? "",
          groupLabel: item.user_full_name ?? t`Work Expense`,
        };
      default:
        return {
          label: item?.user_full_name ?? "",
          groupLabel: item.time_tracking_work_type_title,
        };
    }
  };

  const rows = reports.map((i) => {
    return {
      amount: i.amount,
      invoiced_amount: i.invoiced_amount,
      hourly_rate: i.hourly_rate,
      internal_hourly_rate: i.internal_hourly_rate,
      tracked_time: i.tracked_time,
      personal_expense: i.personal_expense?.denominatedValue,
      personal_profitability: i.personal_profitability?.denominatedValue,
      invoiced_personal_profitability:
        i.invoiced_personal_profitability?.denominatedValue,
      invoiced_seconds: i.invoiced_seconds,
      overserviced_seconds: i.overserviced_seconds,
      uninvoiced_seconds: i.uninvoiced_seconds,
      ...pickLabels(i, column),
    };
  });

  const grouped = groupBy(rows, "groupLabel");

  return Object.entries(grouped).map(([groupLabel, items]) => {
    const firstItem = items[0];
    let firstLevelFilter: TimeReportFilter;

    if (column === TimeTrackingProjectReportOrderByColumnEnum.UserFullName) {
      const user = find(reports, (r) => r.user_full_name === groupLabel);
      firstLevelFilter = {
        value: [user?.user_id ?? ""],
        column: TimeTrackingReportsWhereColumn.UserId,
        operator: SqlOperator.In,
      };
    } else if (
      column ===
      TimeTrackingProjectReportOrderByColumnEnum.OurWorkBudgetItemTitle
    ) {
      const i = find(
        reports,
        (r) => r.our_work_budget_item_title === groupLabel,
      );
      firstLevelFilter = {
        value: [i?.our_work_budget_item_id ?? ""],
        column: TimeTrackingReportsWhereColumn.OurWorkBudgetItemId,
        operator: SqlOperator.In,
      };
    } else {
      const i = find(
        reports,
        (r) => r.time_tracking_work_type_title === groupLabel,
      );
      firstLevelFilter = {
        value: [i?.time_tracking_work_type_id ?? ""],
        column: TimeTrackingReportsWhereColumn.WorkTypeId,
        operator: SqlOperator.In,
      };
    }

    const getSubRowFilters = (item: { label: string }) => {
      if (column === TimeTrackingProjectReportOrderByColumnEnum.UserFullName) {
        const i = find(reports, { time_tracking_work_type_title: item.label });
        return {
          value: [i?.time_tracking_work_type_id ?? ""],
          column: TimeTrackingReportsWhereColumn.WorkTypeId,
          operator: SqlOperator.In,
        };
      } else if (
        column ===
        TimeTrackingProjectReportOrderByColumnEnum.OurWorkBudgetItemTitle
      ) {
        const i = find(reports, { user_full_name: item.label });
        return {
          value: [i?.user_id ?? ""],
          column: TimeTrackingReportsWhereColumn.UserId,
          operator: SqlOperator.In,
        };
      } else {
        const i = find(reports, { user_full_name: item.label });
        return {
          value: [i?.user_id ?? ""],
          column: TimeTrackingReportsWhereColumn.UserId,
          operator: SqlOperator.In,
        };
      }
    };

    return {
      id: `group-${groupLabel}`,
      label: groupLabel,
      image:
        column === TimeTrackingProjectReportOrderByColumnEnum.UserFullName
          ? reports.find((r) => r.user_full_name === groupLabel)?.image
          : undefined,
      tracked_time: sumBy(items, "tracked_time"),
      amount: sumBy(items, "amount"),
      invoiced_amount: sumBy(items, "invoiced_amount"),
      hourly_rate: firstItem.hourly_rate,
      internal_hourly_rate: firstItem.internal_hourly_rate,
      personal_expense: sumBy(items, (i: any) => i.personal_expense ?? 0),
      personal_profitability: sumBy(
        items,
        (i: any) => i.personal_profitability ?? 0,
      ),
      invoiced_personal_profitability: sumBy(
        items,
        (i: any) => i.invoiced_personal_profitability ?? 0,
      ),
      invoiced_seconds: sumBy(items, (i: any) => i.invoiced_seconds ?? 0),
      overserviced_seconds: sumBy(
        items,
        (i: any) => i.overserviced_seconds ?? 0,
      ),
      uninvoiced_seconds: sumBy(items, (i: any) => i.uninvoiced_seconds ?? 0),
      firstLevelFilter,
      __subRows: items.map((item, idx) => ({
        id: `${groupLabel}-${idx}`,
        label: item.label,
        tracked_time: item.tracked_time,
        amount: item.amount,
        invoiced_amount: item.invoiced_amount,
        personal_expense: item.personal_expense,
        personal_profitability: item.personal_profitability,
        invoiced_personal_profitability: item.invoiced_personal_profitability,
        invoiced_seconds: item.invoiced_seconds,
        overserviced_seconds: item.overserviced_seconds,
        uninvoiced_seconds: item.uninvoiced_seconds,
        secondLevelFilter: getSubRowFilters(item),
      })),
    };
  });
}

// Transform breakdown groups into DataTable row format
export function transformBreakdownToRows(
  groups: ProjectBreakdownGroupModel[],
): TimeReportRow[] {
  return groups.map((group) => ({
    id: group.id,
    title: group.title,
    tracked_time: group.tracked_time,
    tracked_amount: group.tracked_amount,
    budgeted: group.budgeted,
    budgeted_time: group.budgeted_time,
    personal_expenses: group.personal_expenses,
    profit: group.profit,
    progressBarSegments: group.progressBarSegments,
    showBudgetWarning: group.showBudgetWarning,
    budgetDifference: group.budgetDifference,
    __subRows: group.time_breakdown.map(
      (breakdown: ProjectBreakdownItemModel) => ({
        id: breakdown.id,
        title: breakdown.title,
        tracked_time: breakdown.tracked_time,
        tracked_amount: breakdown.tracked_amount,
        budgeted: breakdown.budgeted,
        budgeted_time: breakdown.budgeted_time,
        personal_expenses: breakdown.personal_expenses,
        profit: breakdown.profit,
        progressBarSegments: breakdown.progressBarSegments,
        budgetedField: breakdown.budgetedField,
        handleUpdateBudget: breakdown.handleUpdateBudget,
      }),
    ),
  }));
}
